import React, { useContext, useEffect } from 'react';
import RenderAlignedHtml from '@bbkPricingPage/components/card/components/aligned-html-render';
import { PricingTableContext } from '@bbkPricingPage/context/pricing-table-context';
import {
  getAddonDefaultQuantity,
  getAddonQuantity,
  getApplicableAddonsOrCharges,
  getMandatoryAddonIds,
  getOptimalPrice,
  getRecommendedAddonIds,
  initializeSelectedAddonsForPlan,
} from '@bbkPricingPage/helpers/addon-helper';
import { findMatchingFrequency } from '@bbkPricingPage/lib/price-utils';
import {
  GroupedBy,
  ItemType,
  type Discount,
  type Frequency,
  type Subscription,
} from '@bbkPricingPage/types/pricing-table-types';
import {
  type ActiveItemPrice,
  type PlanAddonsMap,
} from '../plan-customisation';
import { AddonSection } from './addon-section';
import { PlanDetail } from './plan-details';

export const CustomisePlan = ({
  selectedPlanPrice,
  plansSelectedAddons,
  subscription,
  applicableDiscounts,
  setPlansSelectedAddons,
}: {
  frequency?: Frequency;
  selectedPlanPrice: ActiveItemPrice;
  plansSelectedAddons: PlanAddonsMap;
  subscription?: Subscription;
  applicableDiscounts?: Discount[];
  setPlansSelectedAddons: React.Dispatch<React.SetStateAction<PlanAddonsMap>>;
}) => {
  const { pricingTable, currency, priceVariantId, frequency } =
    useContext(PricingTableContext);
  const allAddons = pricingTable?.addons ?? [];
  const frequencies = pricingTable?.frequencies ?? [];

  const selectedPlan = selectedPlanPrice.item;
  const selectedItemPrice = selectedPlanPrice.price;

  const priceMatchedFrequency = findMatchingFrequency(
    selectedItemPrice,
    frequencies
  ) as Frequency;
  const effectivePriceFrequency =
    pricingTable?.grouped_by === GroupedBy.item_frequency
      ? priceMatchedFrequency
      : (frequency ?? priceMatchedFrequency);

  const updateSelectedAddonsForCurrentPlan = (
    newAddons: Map<string, { addonId: string; quantity: number }>
  ) => {
    if (!newAddons) return;
    const filteredAddons = newAddons;
    setPlansSelectedAddons((prev) => ({
      ...prev,
      [selectedItemPrice.billing_system_id]: filteredAddons,
    }));
  };

  useEffect(() => {
    if (!plansSelectedAddons[selectedItemPrice.billing_system_id]) {
      const newSelectedAddons = initializeSelectedAddonsForPlan(
        selectedPlan,
        allAddons,
        currency.currency_code,
        effectivePriceFrequency,
        priceVariantId,
        subscription,
        selectedPlanPrice?.isSubscribedPrice ?? false
      );
      updateSelectedAddonsForCurrentPlan(newSelectedAddons);
    }
  }, [selectedItemPrice.billing_system_id]);

  useEffect(() => {
    if (subscription?.subscription_items?.length) {
      const newSelectedAddons = initializeSelectedAddonsForPlan(
        selectedPlan,
        allAddons,
        currency.currency_code,
        effectivePriceFrequency,
        priceVariantId,
        subscription,
        selectedPlanPrice?.isSubscribedPrice
      );
      // Only update if the new addons are different from the current ones
      updateSelectedAddonsForCurrentPlan(newSelectedAddons);
    }
  }, [subscription]);

  const selectedAddons =
    plansSelectedAddons[selectedItemPrice.billing_system_id] ?? new Map();

  const toggleAddon = (addonBillingSystemId: string) => {
    const currentSelectedAddons =
      plansSelectedAddons[selectedItemPrice.billing_system_id] ?? new Map();
    const newSelectedAddons = new Map(currentSelectedAddons);
    if (newSelectedAddons.has(addonBillingSystemId)) {
      newSelectedAddons.delete(addonBillingSystemId);
    } else {
      const defaultQuantity = getAddonDefaultQuantity(
        selectedPlan,
        addonBillingSystemId
      );
      newSelectedAddons.set(addonBillingSystemId, {
        addonId: addonBillingSystemId,
        quantity: defaultQuantity,
      });
    }
    updateSelectedAddonsForCurrentPlan(newSelectedAddons);
  };

  const updateAddonQuantity = (
    addonBillingSystemId: string,
    quantity: number
  ) => {
    const currentSelectedAddons =
      plansSelectedAddons[selectedItemPrice.billing_system_id] ?? new Map();
    const newSelectedAddons = new Map(currentSelectedAddons);

    if (newSelectedAddons.has(addonBillingSystemId)) {
      newSelectedAddons.set(addonBillingSystemId, {
        addonId: addonBillingSystemId,
        quantity,
      });
      updateSelectedAddonsForCurrentPlan(newSelectedAddons);
    }
  };

  const mandatoryAddonIds = getMandatoryAddonIds(selectedPlan);
  const recommendedAddonIds = getRecommendedAddonIds(selectedPlan);

  const applicableCharges = getApplicableAddonsOrCharges({
    allAddons,
    selectedPlanPrice,
    priceVariant: priceVariantId,
    subscription,
    isSubscribedPrice: selectedPlanPrice?.isSubscribedPrice,
    itemType: ItemType.charge,
  });

  const mandatoryApplicableAddons = getApplicableAddonsOrCharges({
    allAddons,
    selectedPlanPrice,
    priceVariant: priceVariantId,
    subscription,
    isSubscribedPrice: selectedPlanPrice?.isSubscribedPrice,
    itemType: ItemType.addon,
    frequency: effectivePriceFrequency,
  });

  const attachedAddonIds = selectedPlan.attached_addons
    .filter((aa) => aa.item_type !== ItemType.charge) // Exclude charges
    .map((aa) => aa.billing_system_id);

  const recommendedApplicableAddons = allAddons
    .filter(
      (addon) =>
        attachedAddonIds.includes(addon.billing_system_id) &&
        recommendedAddonIds.includes(addon.billing_system_id)
    )
    .map((addon) => {
      const addonPrice = getOptimalPrice({
        prices: addon.prices,
        currency: currency.currency_code,
        itemId: selectedPlan.billing_system_id,
        itemType: addon.item_type,
        frequency: effectivePriceFrequency,
        variantId: priceVariantId,
        parentPeriod: selectedItemPrice.period,
        parentPeriodUnit: selectedItemPrice.period_unit,
      });
      if (!addonPrice) return null;

      return {
        addon,
        price: addonPrice,
        quantity: getAddonQuantity(addon, selectedPlan, selectedAddons) ?? 1,
      };
    })
    .filter((item) => item !== null);

  const mandatoryAddonsForSection = mandatoryApplicableAddons.map((item) => ({
    id: item.addon.billing_system_id,
    name: item.addon.customization?.name || '',
    description: item.addon.customization?.description,
    price: item.price,
    quantity: item.quantity,
    isMandatory: true,
    isCharge: false,
  }));

  const recommendedAddonsForSection = recommendedApplicableAddons.map(
    (item) => ({
      id: item!.addon.billing_system_id,
      name: item!.addon.customization?.name || '',
      description: item!.addon.customization?.description,
      price: item!.price,
      quantity: item!.quantity,
      isMandatory: false,
      isCharge: false,
    })
  );

  const chargesForSection = applicableCharges.map((item) => ({
    id: item.addon.billing_system_id,
    name: item.addon.customization?.name || '',
    description: item.addon.customization?.description,
    price: item.price,
    quantity: item.quantity,
    isMandatory: false,
    isCharge: true,
  }));

  return (
    <>
      <PlanDetail
        selectedPlanPrice={selectedPlanPrice}
        frequency={effectivePriceFrequency}
        applicableDiscounts={applicableDiscounts?.filter(
          (discount) =>
            discount.item_price_id === selectedItemPrice?.billing_system_id
        )}
        priceDisplayOrientation={
          pricingTable?.customization?.frequency_orientation
        }
        subscription={subscription}
      />

      {selectedPlan && (
        <AddonSection
          title={
            <RenderAlignedHtml
              value={
                pricingTable?.customization?.plan_customization
                  ?.mandatory_addon_title ?? ''
              }
            />
          }
          applicableAddons={mandatoryAddonsForSection}
          applicableCharges={chargesForSection}
          frequency={effectivePriceFrequency}
          selectedAddons={selectedAddons}
          toggleAddon={toggleAddon}
          updateAddonQuantity={updateAddonQuantity}
          selectedPlanId={selectedPlan.billing_system_id}
        />
      )}

      {selectedPlan && (
        <AddonSection
          title={
            <RenderAlignedHtml
              value={
                pricingTable?.customization?.plan_customization
                  ?.recommended_addon_title ?? ''
              }
            />
          }
          applicableAddons={recommendedAddonsForSection}
          frequency={effectivePriceFrequency}
          selectedAddons={selectedAddons}
          toggleAddon={toggleAddon}
          updateAddonQuantity={updateAddonQuantity}
          selectedPlanId={selectedPlan.billing_system_id}
        />
      )}
    </>
  );
};
